import React from 'react';
import { Modal } from '@digitools/honest-ui';
import { OpenInNewUpIcon } from '@digitools/honest-ui/icons';
import LincolnMark from 'src/assets/lincoln-mark.svg';
import useTranslate from 'src/hooks/useTranslate';
import { getThirdPartyAdditionalInfo, getThirdPartyModalText, VISION_COMPANY_NAME } from './i18n/ThirdPartyModalText';
import styles from './ThirdPartyModal.scss';
const ThirdPartyModal = ({ opened, close, navigationCallback, isVision = false, 'data-testid': dataTestId = 'thirdPartyModal', }) => {
    const { t } = useTranslate();
    const text = getThirdPartyModalText(isVision);
    // Destination-specific approved paragraph (the Spectera support number) — code-defined copy keyed
    // off the same flag, so there is a single switch between the two wording modes.
    const additionalInfo = getThirdPartyAdditionalInfo(isVision);
    const handleGoToSite = () => {
        navigationCallback();
        close();
    };
    const title = `${t(text.titleLead)}${t(text.titleTrail)}`;
    return (<Modal className={styles.modal} 
    // Figma reads clearly landscape; the library default is min(600px, 100%), which renders
    // noticeably narrower/squarer than the design.
    size={'52rem'} isOpen={opened} 
    // Modal reports every close intent (X, Cancel, Escape, backdrop) as onOpenChange(false).
    // The wrapper keeps the nullary `close` API that RouteCheckProvider already shares with
    // its sibling NavigationModal, so the call site is unchanged.
    onOpenChange={(isOpen) => {
            if (!isOpen)
                close();
        }} onSubmit={handleGoToSite} title={<>
          {t(text.titleLead)}
          {/* The legacy title carries the ® superscript (mirrors the old ExitModal). The
                redesigned vision title per Figma / approved copy has no ® after
                "Lincoln Financial's", so only render the mark in legacy mode. */}
          {!isVision && <sup>&#174;</sup>}
          {t(text.titleTrail)}
        </>} 
    // `title` is a node (the conditional ®), so Modal requires a plain-text accessible name.
    titleLabel={title} submitLabel={t(text.goToSiteButtonText)} submitIcon={<OpenInNewUpIcon />} cancelLabel={t(text.cancelButtonText)} 
    // Modal reads the close button as `${closeLabel} ${titleLabel}`, so this has to be
    // translated too or a Spanish user hears "Close Está saliendo…".
    closeLabel={t(text.closeButtonText)} 
    // The mark's maroon ring is part of the asset, so there is nothing to style here. 96px is
    // what makes Modal's own overlap (--spacing-xl-4, 3rem) land at half the badge, straddling
    // the card's top edge — see the note in ThirdPartyModal.scss before changing it.
    graphic={<LincolnMark width={96} height={96}/>} wave data-testid={dataTestId} submitButtonProps={{
            'data-testid': `${dataTestId}-go-to-site`,
            // Vision mode adds the destination name for context ("Go to site Spectera, Inc.").
            // Legacy mode ("Continue") already has clear visible text, so no aria-label (adding
            // one would just duplicate the visible label — see react-accessibility redundancy rule).
            'aria-label': isVision ? `${t(text.goToSiteButtonText)} ${VISION_COMPANY_NAME}` : undefined,
        }} cancelButtonProps={{ 'data-testid': `${dataTestId}-cancel` }} closeButtonProps={{ 'data-testid': `${dataTestId}-close` }}>
      <p>{t(text.directedTo)}</p>
      <p>{t(text.privacyPractices)}</p>
      {additionalInfo && <p>{t(additionalInfo)}</p>}
    </Modal>);
};
export default ThirdPartyModal;
